import React, {ReactElement, useCallback, useEffect} from 'react';
import {
  Stack,
  Box,
  MenuItem,
  Tooltip,
  IconButton,
  Tab,
  Tabs,
  Grid
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useTheme} from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import {getSearchForm} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';

import {PatientAccountingWrapper} from './PatientAccountingStyles';

import CustomBreadcrumbs from 'src/minimal-components/custom-breadcrumbs/custom-breadcrumbs';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useAppContext} from '@context/AppContext';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import CustomPopover, {usePopover} from 'src/minimal-components/custom-popover';
import ModuleSearchPage from '@modules/home/main/modules-menu/modules-search-page/ModuleSearchPage';
import PaymentAutoPost from './payment/auto-post/PaymentAutoPost';
import ARWorklist from './ar-workdown/ar-worklist/ARWorklist';
import PaymentManualPost from './payment/manual-post/PaymentManualPost';
import FormConstants from '@shared/constants/FormConstants';
import RCMDashboardComponent from '../rcm-dashboard/RCMDashboardComponent';
import {Controller, useForm} from 'react-hook-form';
import {FormControlDiv} from '../modules-search-page/ModuleSearchPageStyle';
import SelectInput from '@components/form/select-input/SelectInput';
import PaymentReconciliation from './report/payment-reconciliation/PaymentReconciliation';

const paymentTabConstant = FormConstants.RCM_PATIENT_ACCOUNTING.TABS.PAYMENT;

const PatientAccountingPage = (props: any): React.ReactElement => {
  const {
    appState: {applicationName}
  } = useAppContext();
  const theme = useTheme();
  const [loader, setLoader] = React.useState<boolean>(false);
  const [tab, setTab] = React.useState<string>('');
  const [patientData, setPatientData] = React.useState<any>({});
  const [paymentPage, setPaymentPage] = React.useState<string>('');
  const [paymentPageLabel, setPaymentPageLabel] = React.useState<string>('');

  const methods = useForm({});

  const {
    control,
    handleSubmit,
    reset,
    formState: {errors, isSubmitting}
  } = methods;

  const [rcmDashboardState, setRcmDashboardState] = React.useState<any>({
    category: ''
  });

  const {enqueueSnackbar} = useSnackbar();
  const popover = usePopover();

  const init = async () => {
    try {
      if (props.module) {
        setLoader(true);
        const res = await getSearchForm(applicationName, props.module);
        setPatientData({...res.searchForm});
        const subSections = res?.searchForm?.sub_sections;
        setTab(subSections[0]?.name);
        setLoader(false);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setLoader(false);
    }
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
// console.log('patientACounting----')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getPatientPageData() {
    const patientPageData = patientData?.sub_sections?.find(
      (section: any) => section.name === 'patient'
    );
    return {...patientPageData};
  }

  function setActiveTab(e: any, tabName: string) {
    if ([paymentTabConstant].includes(tabName)) {
      popover.onOpen(e);
    } else {
      setTab(tabName);
      popover.onClose();
      setPaymentPage('');
      setPaymentPageLabel('');
    }
  }

  function handleTabChange(e: any, value: string) {
    setActiveTab(e, value);
  }

  function updatePaymentPage(pageName: string, pageLabel: string) {
    setTab(paymentTabConstant);
    setPaymentPage(pageName);
    setPaymentPageLabel(pageLabel);
    popover.onClose();
  }

  function renderPatientPage() {
    return (
      <ModuleSearchPage
        module={props.module}
        tab={tab}
        menuItem={props.menuItem}
        uiFieldsData={patientData?.sub_sections?.find(
          (section: any) => section.name === 'patient'
        )}
        setParentState={() => {}}
      />
    );
  }

  const renderSelectFields = useCallback(
    (inputName: string, item: any): ReactElement => {
      let optionsList: any = [];

      optionsList = item.static_dropdown_value
        ? item.static_dropdown_values
        : item?.drop_down_options ||
          item?.drop_down_options?.[0]?.report_type ||
          [];

      return (
        <Grid container justifyContent="start" sx={{mt: 2}}>
          <Grid item xs={2}>
            <Controller
              name={inputName}
              control={control}
              defaultValue={
                inputName === FormConstants.PURGE_TERMS.RETENTION_YEARS ||
                inputName === FormConstants.PURGE_TERMS.RETENTION_MONTHS
                  ? '0'
                  : ''
              }
              render={({field: {value, onChange}}) => (
                <FormControlDiv>
                  <SelectInput
                    enableDefaultOption
                    options={optionsList}
                    label={
                      item.heading[0]
                        ? item.heading?.[0] || item.heading?.[1]
                        : ''
                    }
                    placeholder={item.placeholder}
                    name={inputName}
                    value={value || ''}
                    fullWidth
                    onChange={(event: any) => {
                      onChange(event);
                      setRcmDashboardState({
                        [event.target.name]: event.target.value
                      });
                    }}
                    optionValueKey={'value'}
                    optionLabelKey={'label'}
                  />
                </FormControlDiv>
              )}
            />
          </Grid>
        </Grid>
      );
    },
    [rcmDashboardState]
  );

  const renderDashboardPage = useCallback(() => {
    const dashboardSection = patientData?.sub_sections?.find(
      (section: any) => section.name === 'dashboard'
    );
    const selected_dashboard = dashboardSection.sub_sections?.find(
      (item: any) =>
        item.category == `${rcmDashboardState.category}`.toLowerCase()
    );
    return (
      <>
        {renderSelectFields('category', dashboardSection?.fields?.category)}
        {selected_dashboard ? (
          <ModuleSearchPage
            module={props.module}
            tab={tab}
            menuItem={props.menuItem}
            uiFieldsData={selected_dashboard}
            gridTemplateColumns={{
              xs: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)'
            }}
            setParentState={setRcmDashboardState}
          />
        ) : null}
        {rcmDashboardState?.category?.length ? (
          <RCMDashboardComponent
            data={rcmDashboardState}
            dashboard_type={rcmDashboardState?.category}
            uiFieldsData={selected_dashboard}
          />
        ) : null}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rcmDashboardState,
    patientData?.sub_sections,
    props.module,
    props.menuItem,
    tab
  ]);

  function renderTabsUI() {
    const subSections = patientData?.sub_sections;
    const settingOptions =
      patientData?.sub_sections?.find(
        (section: any) => section.name === paymentTabConstant
      )?.dropdown_values || [];
    return (
      <>
        <Tabs
          value={tab}
          sx={{
            mb: {xs: 3, md: 2}
          }}
          TabIndicatorProps={{
            style: {
              backgroundColor: theme.palette.primary.lighter
            }
          }}>
          {subSections?.map((section: any, index: any) => {
            return (
              <Tab
                onClick={(e: any) => handleTabChange(e, section?.name)}
                key={section?.name || index}
                label={section?.label}
                value={section?.name}
                wrapped
                sx={{
                  color: theme.palette.primary.lighter
                }}
              />
            );
          })}
        </Tabs>
        <CustomPopover
          arrow="top-left"
          open={popover.open}
          onClose={popover.onClose}
          sx={{width: 200, p: 0}}>
          <Box sx={{p: 2, pb: 1.5}}>
            {settingOptions.map((option: any) => {
              return (
                <MenuItem
                  selected={option.name === paymentPage}
                  key={option.name}
                  onClick={() => updatePaymentPage(option.name, option.label)}
                  sx={{
                    color:
                      option.name === paymentPage
                        ? theme.palette.primary.lighter
                        : 'unset'
                  }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem !important',
                      fontWeight: theme.typography['fontWeightSemiBold']
                    }}>
                    {option.label}
                  </Typography>
                </MenuItem>
              );
            })}
          </Box>
        </CustomPopover>
      </>
    );
  }

  function formatModuleName(moduleName: string): string {
    if (!moduleName || moduleName.length <= 3) {
      return (moduleName || '').toUpperCase();
    }

    const [firstTwoLetters, rest] = moduleName.split('-');

    // Capitalize first two letters if module name contains only two letters before hyphen
    if (firstTwoLetters.length === 2) {
      const formattedFirstTwoLetters = firstTwoLetters.toUpperCase();
      return `${formattedFirstTwoLetters} ${
        rest.charAt(0).toUpperCase() + rest.slice(1)
      }`;
    }

    const words = moduleName.replace(/_/g, ' ').split(/-| /);

    const formattedModuleName = words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return formattedModuleName;
  }

  const breadcrumbsLinks = [
    {
      name: formatModuleName(props?.module)
    },
    {
      name: formatModuleName(tab)
    },
    ...(paymentPage?.length ? [{name: formatModuleName(paymentPage)}] : [])
  ];

  const getPatientPaymentConfig = () => {
    return patientData?.sub_sections?.find(
      (section: any) => section.name === paymentTabConstant
    );
  };

  const getPatientReportConfig = () => {
    return patientData?.sub_sections?.find(
      (section: any) =>
        section.name === FormConstants.RCM_PATIENT_ACCOUNTING.TABS.REPORT
    );
  };

  return (
    <div>
      {!loader ? (
        <PatientAccountingWrapper>
          {renderTabsUI()}
          <CustomBreadcrumbs links={breadcrumbsLinks || []} />
          {tab === FormConstants.RCM_PATIENT_ACCOUNTING.TABS.DASHBOARD
            ? renderDashboardPage()
            : null}
          {tab === FormConstants.RCM_PATIENT_ACCOUNTING.TABS.PATIENT
            ? renderPatientPage()
            : null}
          {tab === FormConstants.RCM_PATIENT_ACCOUNTING.TABS.PAYMENT && (
            <>
              {paymentPage ===
                FormConstants.RCM_PATIENT_ACCOUNTING.PAYMENT.AUTO_POST && (
                <PaymentAutoPost
                  module={props.module}
                  sub_module={
                    FormConstants.RCM_PATIENT_ACCOUNTING.PAYMENT.AUTO_POST
                  }
                  patientAccountingConfig={patientData}
                  paymentConfig={getPatientPaymentConfig()}
                  paymentPage={paymentPage}
                  paymentPageLabel={paymentPageLabel}
                />
              )}
              {paymentPage ===
                FormConstants.RCM_PATIENT_ACCOUNTING.PAYMENT.MANUAL_POST && (
                <PaymentManualPost
                  module={props.module}
                  sub_module={
                    FormConstants.RCM_PATIENT_ACCOUNTING.PAYMENT.MANUAL_POST
                  }
                  tab={tab}
                  patientAccountingConfig={patientData}
                  paymentPage={paymentPage}
                  paymentPageLabel={paymentPageLabel}
                  paymentConfig={getPatientPaymentConfig()}
                  menuItem={props.menuItem}
                />
              )}
            </>
          )}
          {tab === FormConstants.RCM_PATIENT_ACCOUNTING.TABS.AR_WORKDOWN && (
            <ARWorklist
              patientAccountingConfig={patientData}
              module={props.module}
            />
          )}
          {tab === FormConstants.RCM_PATIENT_ACCOUNTING.TABS.REPORT && (
            <PaymentReconciliation
              tab={tab}
              module={props.module}
              patientAccountingConfig={patientData}
              reportConfig={getPatientReportConfig()}
              paymentConfig={getPatientPaymentConfig()}
            />
          )}
        </PatientAccountingWrapper>
      ) : (
        <Stack
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          minHeight={'80vh'}>
          <LoadingScreen />
        </Stack>
      )}
    </div>
  );
};

export default PatientAccountingPage;
